import { Crown, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useState } from "react";

/**
 * NK+ premium subscription badge. Icon-based, gold, not emoji.
 * Usage: <PremiumBadge active={user?.nk_plus_active} size="sm" />
 */
export function PremiumBadge({ active, size = "sm", showLabel = false, className = "" }) {
  if (!active) return null;
  const iconSize = size === "lg" ? 16 : size === "md" ? 13 : 11;
  return (
    <span
      className={`inline-flex items-center gap-1 align-middle ${className}`}
      title="NK+ member"
      data-testid="nk-plus-badge"
    >
      <span
        className="inline-flex items-center justify-center rounded-[3px]"
        style={{
          width: iconSize + 6,
          height: iconSize + 6,
          background: "linear-gradient(135deg, #D4A24C 0%, #F4D08A 100%)",
          color: "#0A0A0B",
          boxShadow: "0 0 0 1px rgba(212,162,76,0.4)",
        }}
        aria-label="NK+ member"
      >
        <Sparkle size={iconSize} weight="fill" />
      </span>
      {showLabel && (
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-nk-gold">NK+</span>
      )}
    </span>
  );
}

/** Larger format used in profile / settings pages. */
export function PremiumCard({ active }) {
  const [busy, setBusy] = useState(false);
  const upgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout/nk-plus", {
        package_id: "nk_plus_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
      setBusy(false);
    }
  };
  return (
    <div
      className={`nk-card p-5 border ${active ? "border-nk-gold/50" : "border-nk-line"}`}
      data-testid="nk-plus-card"
    >
      <div className="flex items-center gap-3 mb-2">
        <span
          className="inline-flex items-center justify-center rounded-sm"
          style={{
            width: 32, height: 32,
            background: active ? "linear-gradient(135deg, #D4A24C 0%, #F4D08A 100%)" : "rgba(255,255,255,0.04)",
            color: active ? "#0A0A0B" : "rgba(255,255,255,0.4)",
            border: active ? "none" : "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Crown size={18} weight={active ? "fill" : "regular"} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-lg font-medium">NK+ Membership</p>
          <p className="text-[11px] text-nk-mute font-mono uppercase tracking-wider">
            {active ? "Active" : "Inactive · $5 / 30 days"}
          </p>
        </div>
        {!active && (
          <button
            data-testid="nk-plus-upgrade-btn"
            onClick={upgrade}
            disabled={busy}
            className="px-4 py-2 bg-nk-gold text-nk-bg text-sm font-medium rounded-sm hover:opacity-90 transition-opacity disabled:opacity-50"
          >{busy ? "Opening checkout…" : "Upgrade"}</button>
        )}
      </div>
      <ul className="text-xs text-nk-mute space-y-1 mt-3 list-disc pl-4">
        <li>Ad-free guarantee, always.</li>
        <li>Premium icon badge next to your name in every public list.</li>
        <li>Catch-up NK Daily attempt — replay today's questions if you missed live.</li>
        <li>Priority support during championship season.</li>
      </ul>
      {!active && (
        <p className="text-[10px] text-nk-dim mt-3 font-mono">
          Secure Stripe checkout · cards accepted globally · test mode in this preview.
        </p>
      )}
    </div>
  );
}

export default PremiumBadge;
